import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Settings, Ruler, Hammer, ShieldCheck, Leaf, Clock, FileText, Download, Info, Eye, Phone, Mail, Check } from 'lucide-react';
import Layout from '../components/Layout';
import ContactForm from '../components/ContactForm';
import { getOptimizedImageUrl } from '../lib/utils';

const benefits = [
  {
    icon: Clock,
    title: 'Hurtig Proces',
    description: 'Ingen ventetid på betonhærdning. Vi monterer og du bygger videre med det samme.'
  },
  {
    icon: Leaf,
    title: 'Bæredygtighed',
    description: 'Minimal CO2-udledning og ingen bortkørsel af jord. Skruerne kan genanvendes 100%.'
  },
  {
    icon: ShieldCheck,
    title: 'Garanteret Styrke',
    description: 'Hver skrue testes for bæreevne under monteringen. Dokumenteret stabilitet.'
  },
  {
    icon: Ruler,
    title: 'Millimeter Præcision',
    description: 'Laserstyret montering sikrer at fundamentet sidder præcis hvor det skal.'
  }
];

const steps = [
  {
    number: '01',
    title: 'Rådgivning',
    description: 'Vi gennemgår dit projekt og vurderer jordbundsforholdene for at vælge de rette skruer.'
  },
  {
    number: '02',
    title: 'Opmåling',
    description: 'Vi afsætter præcise punkter med laserudstyr, så fundamentet passer perfekt til din tegning.'
  },
  {
    number: '03',
    title: 'Montering',
    description: 'Vores specialmaskiner borer skruerne ned i de bærende jordlag uden støj og vibrationer.'
  },
  {
    number: '04',
    title: 'Klar til byg',
    description: 'Fundamentet er færdigt. Du kan nu montere remme, bjælker eller moduler direkte på skruerne.'
  }
];

const screwTypes = [
  {
    id: 'screw-1200',
    title: 'Ø76 x 1200 x 3,0 mm',
    description: 'Et alsidigt og stærkt skruefundament, der er ideelt til mindre og mellemstore projekter. Giver en fantastisk stabilitet i almindelige jordbundsforhold.',
    applications: ['Træterrasser', 'Hegn og stolper', 'Mindre redskabsrum', 'Legetårne'],
    image: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/57c38476-2b6f-461a-9bcc-1d8c5161c5ef.webp',
    drawing: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/54a9997a-d99c-42ae-8fc9-9952d83d0196.png',
    pdfs: [
      { label: 'Teknisk Datablad (PDF)', url: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/c20fe202-501d-4ff9-a0b7-22e9feed00c4.pdf' }
    ],
    specs: {
      diameter: '76,1 mm',
      length: '1200 mm',
      thickness: '3,0 mm',
      material: 'Varmgalvaniseret stål (S235JRH)'
    }
  },
  {
    id: 'screw-1500',
    title: 'Ø76 x 1500 x 4,5 mm',
    description: 'En kraftigere skrue med øget godstykkelse og længde, der sikrer maksimal bæreevne og når dybere ned i jorden. Perfekt til tungere konstruktioner.',
    applications: ['Carporte og garager', 'Større træterrasser', 'Udestuer', 'Erhvervsbyggeri'],
    image: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/0dfbd866-88ca-4f8f-9a5f-1449832bebe0.webp',
    drawing: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/34a78654-9cb6-4b5b-93fb-23a3c5578977.png',
    pdfs: [
      { label: 'Teknisk Datablad (PDF)', url: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/658c4533-9fa1-45a1-8fea-e922ae38f1ee.pdf' },
      { label: 'Datablad for Skruehoved (PDF)', url: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/1baf9c48-923c-45d6-a935-afc89aa175bd.pdf' }
    ],
    specs: {
      diameter: '76,1 mm',
      length: '1500 mm',
      thickness: '4,5 mm',
      material: 'Varmgalvaniseret stål (S235JRH)'
    }
  },
  {
    id: 'screw-89',
    title: 'Ø89 Skruefundament',
    description: 'Vores absolut kraftigste funderingsskrue til de mest krævende opgaver. Designet til store belastninger, herunder helårs- og sommerhuse samt modulbyggeri.',
    applications: ['Sommerhuse', 'Modulbyggeri', 'Tunge erhvervskonstruktioner', 'Overdækninger (Aarhus Streetfood)'],
    image: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/610602ed-3eac-49fc-8f47-e129ed0707b4.jpg',
    drawing: null,
    pdfs: [
      { label: 'Overblik (PDF)', url: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/69a4bc17-2866-4ffa-89b4-d51db897a89f.pdf' },
      { label: 'Basisdel 89x5x2000mm (PDF)', url: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/39ab6425-af00-43eb-bf88-9d59c2f9991d.pdf' },
      { label: 'Forlængerdel 89x5x1500mm (PDF)', url: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/6dff95ef-3e22-46e6-bc83-b8c03544cd16.pdf' },
      { label: 'Flange 350mm (PDF)', url: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/5692b9bb-74b9-4fcc-8886-dc1240ae349d.pdf' },
      { label: 'Flange 350/500/220 (PDF)', url: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/c271a4b8-7dce-47a9-898a-66a0853dd97f.pdf' }
    ],
    isAwaiting: false,
    specs: {
      diameter: '88,9 mm',
      length: 'Efter opgave (op til 4m+)',
      thickness: 'Efter beregning',
      material: 'Varmgalvaniseret stål (S235JRH)'
    }
  }
];

export default function Screws() {
  const [activeViews, setActiveViews] = useState({
    'screw-1200': 'image',
    'screw-1500': 'image',
    'screw-89': 'image'
  });

  const toggleView = (id, view) => {
    setActiveViews(prev => ({
      ...prev,
      [id]: view
    }));
  };

  // Struktureret data for produkter (skruetyper)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Funderingsskruer og Specifikationer",
    "description": "Vores udvalg af professionelle, varmgalvaniserede kvalitetsskruer fra WSF Schroeffundering.",
    "itemListElement": screwTypes.map((screw, index) => ({
      "@type": "Product",
      "position": index + 1,
      "name": screw.title,
      "description": screw.description,
      "image": screw.image,
      "offers": {
        "@type": "Offer",
        "priceCurrency": "DKK",
        "price": "0.00",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "price": "0.00",
          "priceCurrency": "DKK",
          "valueAddedTaxIncluded": true
        },
        "description": "Kontakt os for et skræddersyet tilbud baseret på dine jordbundsforhold og konstruktionsklasse."
      }
    }))
  };

  return (
    <Layout>
      <Helmet>
        <title>Vores Skruer & Specifikationer | CM Skruefundament ApS</title>
        <meta name="description" content="Se vores udvalg af professionelle skruefundamenter fra WSF Schroeffundering. Tekniske tegninger, mål og specifikationer." />
        <link rel="canonical" href="https://cmskruefundament.dk/skruer" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cmskruefundament.dk/skruer" />
        <meta property="og:title" content="Vores Skruer & Specifikationer | CM Skruefundament ApS" />
        <meta property="og:description" content="Se vores udvalg af professionelle skruefundamenter fra WSF Schroeffundering. Tekniske tegninger, mål og specifikationer." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/57c38476-2b6f-461a-9bcc-1d8c5161c5ef.webp" />
        <meta property="og:locale" content="da_DK" />
        <meta property="og:site_name" content="CM Skruefundament ApS" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cmskruefundament.dk/skruer" />
        <meta name="twitter:title" content="Vores Skruer & Specifikationer | CM Skruefundament ApS" />
        <meta name="twitter:description" content="Se vores udvalg af professionelle skruefundamenter fra WSF Schroeffundering. Tekniske tegninger, mål og specifikationer." />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/57c38476-2b6f-461a-9bcc-1d8c5161c5ef.webp" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="bg-slate-900 py-16 md:py-24 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            <div className="lg:col-span-7">
              <span className="text-xs font-bold uppercase tracking-[0.3em] mb-6 block text-amber-400">Teknik & Proces</span>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase leading-tight tracking-tight mb-6">
                Hvad er skruefundamenter?
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 leading-relaxed font-light mb-8">
                Hos <Link to="/" className="font-bold text-white underline decoration-amber-400 hover:text-amber-300 transition-colors">CM Skruefundament</Link> er et skruefundament i al sin enkelhed en stor stålskrue, der bores ned i jorden, 
                til den rammer de bærende lag. Vi leverer et bredt sortiment af <Link to="/jordskruer" className="font-bold text-white underline decoration-amber-400 hover:text-amber-300 transition-colors">jordskruer</Link>, der erstatter den traditionelle betonklods og 
                giver en række markante fordele.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4">
                  <p className="font-bold text-white text-sm mb-1">WSF Schroeffundering</p>
                  <p className="text-xs text-slate-400">Varmgalvaniseret stål (S235JRH) af højeste europæiske standard.</p>
                </div>
                <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4">
                  <p className="font-bold text-white text-sm mb-1">Fuld Dokumentation</p>
                  <p className="text-xs text-slate-400">Testet bæreevne og trækprøver til alle konstruktionsklasser.</p>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-5" id="kontakt-form">
              <ContactForm
                variant="hero"
                defaultService="Jordskruer"
                title="Få rådgivning & tilbud"
                subtitle="Har du spørgsmål til skruedimensioner, geoteknik eller priser? Send en besked her."
              />
            </div>

          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, i) => (
              <div key={i} className="border border-slate-100 p-4 min-[992px]:p-[30px]">
                <benefit.icon className="w-10 h-10 mb-6 text-slate-400" />
                <h3 className="text-xl font-bold uppercase tracking-tight mb-4">{benefit.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screw Types Section */}
      <section className="py-24 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="text-xs font-bold uppercase tracking-[0.3em] mb-4 block text-slate-500">Vores Sortiment</span>
            <h2 className="text-[1.4rem] md:text-[1.6rem] min-[992px]:text-[2.4rem] font-bold uppercase tracking-tight mb-6">
              Skruetyper & Specifikationer
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              Herunder kan du få et indblik i vores skruefundamenter. Vi anvender udelukkende professionelle, 
              varmgalvaniserede kvalitetsskruer fra vores hollandske samarbejdspartner, <strong>WSF Schroeffundering</strong>, 
              hvilket sikrer dig fuld dokumentation og højeste standard.
            </p>
          </div>

          <div className="space-y-20">
            {screwTypes.map((screw) => {
              const view = activeViews[screw.id];
              return (
                <div key={screw.id} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center border-b border-slate-100 pb-20 last:border-0 last:pb-0">
                  {/* Left: Interactive Image/Drawing Viewer */}
                  <div className="lg:col-span-5 flex flex-col gap-4">
                    <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center p-4">
                      {screw.isAwaiting && (
                        <div className="absolute inset-0 bg-slate-900/5 backdrop-blur-xs flex flex-col items-center justify-center p-6 text-center z-10">
                          <Info className="w-12 h-12 text-slate-400 mb-4" />
                          <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Afventer billeder</span>
                          <p className="text-sm text-slate-600 max-w-xs">Vi afventer i øjeblikket billeder og måltegninger af Ø89 skruen.</p>
                        </div>
                      )}

                      {!screw.isAwaiting && (
                        <figure className="w-full h-full flex flex-col items-center justify-center m-0">
                          <img 
                            src={getOptimizedImageUrl(screw.image, { width: 600, quality: 80 })} 
                            alt={`${screw.title} - Professionelt varmgalvaniseret skruefundament fra WSF`} 
                            className="w-full h-full object-contain hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                          <figcaption className="sr-only">
                            {`${screw.title} varmgalvaniseret funderingsskrue - ${screw.specs.material}`}
                          </figcaption>
                        </figure>
                      )}
                    </div>
                  </div>

                  {/* Right: Technical Details & Description */}
                  <div className="lg:col-span-7">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="bg-slate-100 text-slate-800 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-sm">
                        {screw.specs.diameter}
                      </span>
                      {screw.isAwaiting && (
                        <span className="bg-amber-50 text-amber-800 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-sm">
                          Kommende
                        </span>
                      )}
                    </div>

                    <h3 className="text-2xl md:text-3xl font-bold uppercase tracking-tight mb-6 text-slate-900">
                      {screw.title}
                    </h3>

                    <p className="text-slate-600 leading-relaxed mb-8 text-lg">
                      {screw.description}
                    </p>

                    {/* Technical Specs Grid */}
                    <div className="grid grid-cols-2 gap-6 mb-8 bg-slate-50 p-6 rounded-xl border border-slate-100">
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Diameter</span>
                        <span className="font-bold text-slate-800">{screw.specs.diameter}</span>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Længde</span>
                        <span className="font-bold text-slate-800">{screw.specs.length}</span>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Godstykkelse</span>
                        <span className="font-bold text-slate-800">{screw.specs.thickness}</span>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Materiale</span>
                        <span className="font-bold text-slate-800">{screw.specs.material}</span>
                      </div>
                    </div>

                    {/* Applications */}
                    <div className="mb-8">
                      <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Oplagte anvendelsesområder:</h4>
                      <div className="flex flex-wrap gap-2">
                        {screw.applications.map((app, i) => (
                          <span key={i} className="bg-white border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full">
                            {app}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* PDF Download Buttons */}
                    {!screw.isAwaiting && screw.pdfs && screw.pdfs.length > 0 && (
                      <div className="flex flex-col gap-3">
                        <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400">Dokumentation & Datablade:</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {screw.pdfs.map((pdf, idx) => (
                            <a
                              key={idx}
                              href={pdf.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-3 bg-slate-900 text-white px-4 py-3 text-xs font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors rounded-sm"
                            >
                              <Download className="w-4 h-4 shrink-0" />
                              <span className="truncate">{pdf.label}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technical Detail Section */}
      <section className="py-24 bg-slate-50 border-t border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <figure className="m-0">
                <img 
                  src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/9568f20a-a121-428d-9634-fd4063c3cc3a.jpg", { width: 800, quality: 80 })} 
                  alt="Varmgalvaniseret stålskruefundament - teknisk detalje og gevindudformning" 
                  className="w-full shadow-2xl rounded-lg"
                  loading="lazy"
                />
                <figcaption className="mt-3 text-xs text-slate-500 italic">
                  Varmgalvaniseret stålskrue i S235JRH-stål med optimal gevindgeometri for maksimal træk- og trykstyrke.
                </figcaption>
              </figure>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-[1.4rem] md:text-[1.6rem] min-[992px]:text-[2.4rem] font-bold uppercase tracking-tight mb-8">Teknisk overlegenhed</h2>
              <div className="space-y-8">
                <div>
                  <h4 className="text-lg font-bold uppercase tracking-wide mb-2 flex items-center gap-2">
                    <div className="w-2 h-2 bg-slate-900"></div> Materiale
                  </h4>
                  <p className="text-slate-600 leading-relaxed">
                    Vores skruer er udført i varmgalvaniseret stål af højeste kvalitet (S235JRH), 
                    hvilket sikrer en ekstrem korrosionsbestandighed og en levetid på over 100 år i jorden.
                  </p>
                </div>
                <div>
                  <h4 className="text-lg font-bold uppercase tracking-wide mb-2 flex items-center gap-2">
                    <div className="w-2 h-2 bg-slate-900"></div> Belastning
                  </h4>
                  <p className="text-slate-600 leading-relaxed">
                    Vi kan levere skruer der optager alt fra få hundrede kilo til mange tons i både 
                    tryk, træk og horisontal belastning.
                  </p>
                </div>
                <div>
                  <h4 className="text-lg font-bold uppercase tracking-wide mb-2 flex items-center gap-2">
                    <div className="w-2 h-2 bg-slate-900"></div> Anvendelse
                  </h4>
                  <p className="text-slate-600 leading-relaxed">
                    Ideelt til <Link to="/terrasseskruer" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">terrasser</Link>, carporte, sommerhuse, modulbyggeri, skilte, hegn, forankring med <Link to="/jordanker" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">jordanker</Link>, systemer som <Link to="/groundplug" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">GroundPlug</Link> og meget mere.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-[1.4rem] md:text-[1.6rem] min-[992px]:text-[2.4rem] font-bold uppercase tracking-tight mb-6">Processen fra A til Z</h2>
            <p className="text-slate-600 text-lg">
              Vi gør det nemt at få et sikkert fundament. Læs om metoden under <Link to="/skruefundering" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">skruefundering</Link>, se udførte opgaver under <Link to="/projekter" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">vores projekter</Link> eller læs mere <Link to="/om-os" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">om os</Link>.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-slate-100">
            {steps.map((step, i) => (
              <div key={i} className="p-4 min-[992px]:p-[30px] border-r border-b border-slate-100 relative">
                <span className="text-6xl font-black text-slate-100 absolute top-4 right-4">
                  {step.number}
                </span>
                <h3 className="text-xl font-bold uppercase tracking-tight mb-4 relative z-10">{step.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed relative z-10">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Contact Form Section */}
      <section id="tilbud" className="bg-slate-900 py-20 lg:py-24 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-amber-500 mb-4 block">Få et uforpligtende tilbud</span>
              <h2 className="text-[1.4rem] md:text-[1.6rem] min-[992px]:text-[2.4rem] font-bold uppercase tracking-tight mb-6 text-white leading-tight">
                Klar til at vælge det rette skruefundament?
              </h2>
              <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-8">
                Vi rådgiver dig om valg af skruetyper, længder, flangesystemer og bæreevne til netop din jordbund og konstruktion. Udfyld formularen for hurtig beregning og tilbud.
              </p>

              <div className="space-y-4 mb-8">
                <a
                  href="tel:26796139"
                  className="flex items-center gap-4 p-4 bg-slate-800/90 rounded-lg border border-slate-700 hover:border-amber-500 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider font-bold text-slate-400">Teknisk rådgivning</p>
                    <p className="text-lg font-bold text-white">26 79 61 39</p>
                  </div>
                </a>

                <a
                  href="mailto:cs@cmskruefundament.dk"
                  className="flex items-center gap-4 p-4 bg-slate-800/90 rounded-lg border border-slate-700 hover:border-amber-500 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider font-bold text-slate-400">Send forespørgsel</p>
                    <p className="text-base font-bold text-white">cs@cmskruefundament.dk</p>
                  </div>
                </a>
              </div>

              <div className="border-t border-slate-800 pt-6">
                <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Vores fordele</h4>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Højeste kvalitet i varmgalvaniseret stål (S235JRH)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Stort lagerprogram med hurtig levering i hele DK</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Professionel montering eller gør-det-selv vejledning</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
